
import { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Target, Calendar, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const GetStarted = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const features = [
    {
      icon: Target,
      title: "Set Your PACTs",
      description: "Create simple, actionable commitments that fit your lifestyle"
    },
    {
      icon: Calendar,
      title: "Track Daily Progress",
      description: "Mark your daily completions and build consistent habits"
    },
    {
      icon: TrendingUp,
      title: "Build Momentum",
      description: "Watch your streaks grow and celebrate your consistency"
    }
  ];

  const handleGetStarted = () => {
    navigate('/app');
  };

  const handleGoBack = () => {
    navigate('/');
  };

  // Auto-advance animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % features.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-blue to-white">
      <div className="container max-w-md mx-auto px-4 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={handleGoBack}
            variant="ghost"
            className="text-deep-blue/70 hover:text-deep-blue"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>

        {/* Header with Logo */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <img 
              src="/lovable-uploads/4ac166ca-b766-4553-86de-ebfb9d3688a6.png" 
              alt="Show Up! Logo"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-deep-blue mb-3">Show Up!</h1>
          <p className="text-deep-blue/70 text-lg">
            The simplest way to build life-changing habits
          </p>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-soft-blue/50 mb-8">
            <h2 className="text-2xl font-semibold text-deep-blue mb-4">
              Ready to transform your daily routine?
            </h2>
            <p className="text-deep-blue/70 mb-6">
              Show Up! helps you create simple PACTs with yourself and track your progress one day at a time.
            </p>
            
            <div className="w-full bg-soft-blue rounded-full h-2 mb-4">
              <div 
                className="bg-vibrant-orange h-2 rounded-full transition-all duration-1000"
                style={{ width: `${(currentStep + 1) * 33.33}%` }}
              />
            </div>
            
            <p className="text-sm text-deep-blue/60">
              Join thousands who are showing up every day
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-6 mb-12">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`bg-white rounded-xl p-6 shadow-sm border transition-all duration-500 ${
                index <= currentStep 
                  ? 'border-vibrant-orange/50 scale-100 opacity-100' 
                  : 'border-soft-blue/50 scale-95 opacity-70'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  index <= currentStep 
                    ? 'bg-vibrant-orange text-white' 
                    : 'bg-soft-blue text-deep-blue'
                }`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-deep-blue mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-deep-blue/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mb-8">
          <Button
            onClick={handleGetStarted}
            className="bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-xl px-8 py-4 text-lg font-semibold w-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Start Your First PACT
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-deep-blue/60 text-sm mb-2">
            "The secret to getting ahead is to show up - again."
          </p>
          <p className="text-deep-blue/60 text-xs">
            Ready to show up? 💙
          </p>
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
